import { ErrorBoundary } from 'react-error-boundary';
import { useLoaderData, useMatches } from 'react-router';
import { type RequestContext, type SerializeFrom } from '@cx-web/domain-types';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { captureException } from '@sentry/react-router';

import BettingInsights from '~/components/BettingInsights';
import SortingLayout from '~/components/BettingInsights/components/SortingLayout';
import { Error as ErrorComponent } from '~/components/error/Error.tsx';
import { Hero } from '~/components/hero/Hero.tsx';
import SearchWrapper from '~/components/SearchWrapper';
import { type PremiumMarketMoversIndex } from '~/configuration/types';
import { createPremiumMarketMoversConfig } from '~/pages/config';
import { premiumMarketMoversIndex } from '~/search-domain/indexes';

import type { loader as rootLoader } from '../../../root-loader';

import { loader } from './loader';
import { meta } from './meta';

export default function PremiumMarketMoversToolRoute() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  // const { t } = useTranslation();

  const { geolocationInfo, siteConfig } = rootLoaderData;
  const {
    availableProducts,
    currentPath,
    monthlyProducts,
    repub,
    requestContext,
    resourcesUrl,
    subscriptionStatus,
    yearlyProducts,
  } = useLoaderData<typeof loader>();
  const config = createPremiumMarketMoversConfig(requestContext as RequestContext);
  const userHasCancelledSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'CANCELLED',
  );

  const userHasActiveSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'ACTIVE' || status === 'TRIAL',
  );

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const defaultYearlyProduct = getDefaultProduct(yearlyProducts);

  const { repubPath } = getRepubMetaFromRepub(repub);

  return (
    <SortingLayout>
      {defaultMonthlyProduct && defaultYearlyProduct && (
        <Hero
          currentPath={currentPath}
          hasActiveSubscription={userHasActiveSubscription}
          hasCancelledSubscription={userHasCancelledSubscription}
          monthlyProduct={defaultMonthlyProduct}
          repub={repub}
          repubPath={repubPath}
          shouldHideDetailsMobile={true}
          yearlyProduct={defaultYearlyProduct}
        >
          <div style={{ padding: '48px 0' }}>
            <h1>Premium Market Movers</h1>
            <p>Track market movers in real-time, follow the money, and use historical data to understand why.</p>
            <p style={{ marginTop: '16px' }}>
              Stay ahead of the market with our powerful Premium Market Movers tool, designed to highlight the biggest
              steamers and drifters in real time. Backed by trusted historical data, it helps you make smarter, more
              informed betting decisions and spot value as the odds are shifting.
            </p>
            {/* <h1>{t('CX_PRICE_MOVEMENT_TITLE')}</h1>
						<p>{t('CX_PRICE_MOVEMENT_SUBHEADING')}</p> */}
          </div>
        </Hero>
      )}
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureException(error);
          return (
            <ErrorComponent
              indexName={premiumMarketMoversIndex}
              message={(error as Error).message}
              title="Something went wrong"
            />
          );
        }}
      >
        <SearchWrapper<PremiumMarketMoversIndex>
          config={config}
          indexName={premiumMarketMoversIndex}
          repub={repub}
          state={geolocationInfo.subdivisionCode}
          userTZ={requestContext.user.TZ}
        >
          <BettingInsights<PremiumMarketMoversIndex>
            availableProducts={availableProducts}
            bookmakers={siteConfig.bookmakers}
            config={config}
            currentPath={currentPath}
            indexName={premiumMarketMoversIndex}
            redirectPath={currentPath ?? undefined}
            resourcesUrl={resourcesUrl}
            subscriptionStatus={subscriptionStatus}
            userHasCancelledSubscription={userHasCancelledSubscription}
            wallType={config.wallType}
          />
        </SearchWrapper>
      </ErrorBoundary>
    </SortingLayout>
  );
}

export { loader, meta };
